import { types, flow } from 'mobx-state-tree';
import api from 'services/API';
import { Integration } from './types';

export const integrationsInitialState = {
  all: [],
  connectedIntegrationIds: [],
  integrationHealth: [],
  healthChecking: false,
  authValidatorIntegrationNames: null,
  authValidatorBlacklist: [],
};

// Integrations shown under a different label in the POS dropdown only, keyed by
// integrations.name. Labels live in the locales under SystemConfiguration.posDropdownLabels.
const DROPDOWN_LABEL_OVERRIDES = ['harbourtouch'];

// In-flight request for the auth-validator integration list, so concurrent schema
// lookups (desktop + mobile POS panels) share a single GET instead of racing.
let authValidatorIntegrationsRequest = null;

export const integrationsStore = types
  .model({
    all: types.array(Integration),
    // Integration ids that currently have active saved credentials for the loaded
    // establishment. Populated by fetchConnectedIntegrations; drives the connected
    // indicator in the POS dropdown. No credential values are ever stored here.
    connectedIntegrationIds: types.array(types.number),
    // Realtime health results per connected integration: [{ integration_id, name, ok,
    // supported, message }]. Populated by fetchIntegrationHealth. No credential values.
    integrationHealth: types.array(types.frozen()),
    healthChecking: types.optional(types.boolean, false),
    // Names of the integrations the auth-validator knows about (has a credential
    // schema for). null until loaded, or if the list request failed. Used to skip the
    // per-integration schema request for POS types the validator doesn't support, which
    // would otherwise 404 (CWA-3289).
    authValidatorIntegrationNames: types.maybeNull(types.frozen()),
    // Integration names (integrations.name) in the server's AUTH_VALIDATOR_BLACKLIST,
    // delivered alongside the auth-validator integration list. Blacklisted POS types are
    // hidden from the POS dropdown, and if the establishment is already on one the
    // dropdown is locked. Empty until loaded (or if the list request failed).
    authValidatorBlacklist: types.optional(types.frozen(), []),
  })
  .views(self => ({
    getPosById(id) {
      return self.all.find(item => item.id === id);
    },
    isIntegrationConnected(id) {
      return self.connectedIntegrationIds.includes(id);
    },
    isBlacklisted(id) {
      const name = self.getPosById(Number(id))?.name;
      return Boolean(name) && self.authValidatorBlacklist.includes(name);
    },
    // The POS dropdown can't be used to move an establishment off a blacklisted POS.
    isPosSelectionLocked(currentIntegrationId) {
      return self.isBlacklisted(currentIntegrationId);
    },
    // Integrations to list in the POS dropdown, sorted by name. Blacklisted ones are
    // left out, except the currently selected one: the Select still needs a matching
    // option to render its label (the dropdown is locked in that case, so it can't be
    // opened anyway).
    getPosOptions(selectedId) {
      return self.all
        .filter(item => item.id === Number(selectedId) || !self.isBlacklisted(item.id))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },
    // Label for the POS dropdown. Pass the component's i18n `t` so it follows the current
    // language; display_name elsewhere is unchanged.
    getDropdownLabel(integration, t) {
      if (!integration) return '';
      if (t && DROPDOWN_LABEL_OVERRIDES.includes(integration.name)) {
        return t(`SystemConfiguration.posDropdownLabels.${integration.name}`, {
          defaultValue: integration.display_name,
        });
      }
      return integration.display_name;
    },
    // Only cloud integrations have a remote connection to set up (credentials, health
    // light). Local integrations (cli, mssql, file, ...) are configured on-site.
    isCloudIntegration(id) {
      return self.getPosById(id)?.pos_type === 'cloud';
    },
    // Semantic status for an integration's dropdown indicator:
    // 'online' | 'offline' | 'unsupported' | 'checking' | 'connected' | null.
    // Always null for non-cloud integrations — there is no connection to report on.
    getIntegrationStatus(id) {
      if (!self.isCloudIntegration(id)) return null;
      const health = self.integrationHealth.find(item => item.integration_id === id);
      if (health) {
        if (health.supported === false) return 'unsupported';
        return health.ok ? 'online' : 'offline';
      }
      if (self.connectedIntegrationIds.includes(id)) {
        return self.healthChecking ? 'checking' : 'connected';
      }
      return null;
    },
  }))
  .actions(self => ({
    //actions never used
    fetch: flow(function* () {
      try {
        const response = yield api.getIntegrations();
        if (response && response.data && Array.isArray(response.data.result)) {
          self.all.replace(response.data.result);
        }
        return response;
      } catch (err) {
        self.all = [];
        console.error(err);
        return Promise.reject(err);
      }
    }),

    setIntegrations(integrations) {
      self.all.replace(integrations);
    },

    setAuthValidatorIntegrationNames(names) {
      self.authValidatorIntegrationNames = Array.isArray(names) ? names : null;
    },

    setAuthValidatorBlacklist(names) {
      self.authValidatorBlacklist = Array.isArray(names)
        ? names.filter(name => typeof name === 'string' && name)
        : [];
    },

    // Load (once) the list of integrations the auth-validator has a credential schema
    // for, plus the POS blacklist served with it. Resolves to an array of names, or null
    // if the list couldn't be loaded — in which case callers fall back to requesting the
    // schema directly.
    fetchAuthValidatorIntegrations: flow(function* () {
      if (Array.isArray(self.authValidatorIntegrationNames)) {
        return self.authValidatorIntegrationNames;
      }
      if (!authValidatorIntegrationsRequest) {
        authValidatorIntegrationsRequest = api.getAuthValidatorIntegrations();
      }
      try {
        const response = yield authValidatorIntegrationsRequest;
        const list = Array.isArray(response?.data?.integrations)
          ? response.data.integrations
          : null;
        const names = list ? list.map(item => item.name).filter(Boolean) : null;
        self.setAuthValidatorIntegrationNames(names);
        self.setAuthValidatorBlacklist(response?.data?.blacklist);
        return names;
      } catch (error) {
        console.error('Failed to load auth-validator integrations:', error);
        return null;
      } finally {
        authValidatorIntegrationsRequest = null;
      }
    }),

    // Fetch the auth-validator schema for an integration and normalize the
    // field format (auth-validator uses `name`, the frontend expects `key`).
    // Errors are handled here at the store level: logged and resolved to null,
    // which callers already treat as "no schema available".
    //
    // Not every POS type has credential fields (e.g. aloha, restaurantmanager, bbot):
    // the validator answers 404 `unknown_integration` for those. Check the integration
    // list first so we don't fire a request we know will 404, and if one still comes
    // back treat it as "no schema" rather than an error (CWA-3289).
    fetchIntegrationSchema: flow(function* (integrationName) {
      if (!integrationName) return null;

      const known = yield self.fetchAuthValidatorIntegrations();
      if (Array.isArray(known) && !known.includes(integrationName)) {
        return null;
      }

      try {
        const response = yield api.getAuthValidatorIntegrationSchema(integrationName);
        if (!response || !response.data) return null;

        return {
          ...response.data,
          fields: (response.data.fields || []).map(field => ({
            key: field.name,
            label: field.label,
            type: field.type,
            required: field.required,
            placeholder: field.placeholder,
            help: field.help,
            options: field.options,
          })),
        };
      } catch (error) {
        if (
          error?.response?.status === 404 &&
          error.response.data?.code === 'unknown_integration'
        ) {
          // No credential schema for this POS type — nothing to render.
          return null;
        }
        console.error('Failed to fetch integration schema:', error);
        return null;
      }
    }),

    // Load the saved (decrypted) POS credentials for an establishment/integration.
    // The API returns the full establishment_pos_infos row; the decrypted credential
    // fields live under `.credentials`. Return only that object so row columns
    // (id, establishment_id, integration_id) don't leak into the form state and get
    // re-sent — and stored as raw, non-encrypted values — on the next save.
    fetchCredentials: flow(function* (establishmentId, integrationId) {
      try {
        const response = yield api.getPosCredentials(establishmentId, integrationId);
        return response?.data?.result?.credentials || {};
      } catch (error) {
        console.error('Failed to fetch POS credentials:', error);
        return {};
      }
    }),

    // Validate credentials against the auth-validator. Returns a normalized
    // { ok, message } result. Network/HTTP errors are logged here and re-thrown
    // so callers can surface error.response details.
    validateCredentials: flow(function* (integrationName, credentials) {
      try {
        const response = yield api.validateAuthCredentials(integrationName, credentials);
        return {
          ok: !!(response.data && response.data.ok === true),
          message: response.data?.message,
        };
      } catch (error) {
        console.error('Failed to validate credentials:', error);
        throw error;
      }
    }),

    // Persist POS credentials for an establishment/integration. Errors are logged
    // here and re-thrown so callers can show a failure toast.
    saveCredentials: flow(function* (establishmentId, integrationId, credentials) {
      try {
        return yield api.savePosCredentials(establishmentId, integrationId, credentials);
      } catch (error) {
        console.error('Failed to save POS credentials:', error);
        throw error;
      }
    }),

    // Load which integrations have active saved credentials for the establishment, so the
    // UI can flag connected integrations. Returns integration ids only, never values.
    fetchConnectedIntegrations: flow(function* (establishmentId) {
      try {
        const response = yield api.getPosCredentialsStatus(establishmentId);
        const ids = Array.isArray(response?.data?.result) ? response.data.result : [];
        self.connectedIntegrationIds.replace(ids);
        return ids;
      } catch (error) {
        console.error('Failed to load connected integrations:', error);
        self.connectedIntegrationIds.replace([]);
        return [];
      }
    }),

    // Optimistically mark an integration as connected + online (e.g. right after a
    // successful validate-and-save) so the indicator turns green immediately without a
    // page refresh. A subsequent fetchIntegrationHealth confirms this against the server.
    setIntegrationOnline(integrationId) {
      if (!self.connectedIntegrationIds.includes(integrationId)) {
        self.connectedIntegrationIds.push(integrationId);
      }
      const entry = { integration_id: integrationId, ok: true, supported: true, message: null };
      const others = self.integrationHealth.filter(item => item.integration_id !== integrationId);
      self.integrationHealth.replace([...others, entry]);
    },

    // Run realtime validation of the establishment's connected integrations (server-side)
    // and store the per-integration online/offline results. No credential values.
    fetchIntegrationHealth: flow(function* (establishmentId) {
      self.healthChecking = true;
      try {
        const response = yield api.getPosCredentialsHealth(establishmentId);
        const results = Array.isArray(response?.data?.result) ? response.data.result : [];
        self.integrationHealth.replace(results);
        return results;
      } catch (error) {
        console.error('Failed to load integration health:', error);
        self.integrationHealth.replace([]);
        return [];
      } finally {
        self.healthChecking = false;
      }
    }),
  }));
